import { Injectable, InputSignal, Signal } from '@angular/core';
import type {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexGrid,
    ApexLegend,
    ApexNoData,
    ApexNonAxisChartSeries,
    ApexPlotOptions,
    ApexStates,
    ApexStroke,
    ApexTitleSubtitle,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';

import {
    ComputedStatistic,
    FeatureId,
    LineColumnComputedStatisticResult,
    LineColumnComputedStatisticResults,
} from '@connect-services/src/sdk/api';

export const NO_LABEL = 'no-label';

export const collapsedHeightChart = 250;
export const expandedHeightChart = 400;

export const collapsedColumnHeightChart = 350;
export const expandedColumnHeightChart = 450;

export const collapsePieGaugeHeightChart = 150;
export const expandedPieGaugeHeightChart = 220;

const fontFamily = 'DM Sans, sans-serif';

export const commonOptions: BaseChartOptions = {
    chart: {
        events: {},
        redrawOnParentResize: true,
        type: 'bar',
    },
    grid: {
        show: false,
    },
    legend: {
        fontSize: '12px',
    },
    subtitle: {
        offsetX: 5,
        offsetY: 25,
    },
    title: {
        offsetX: 5,
        offsetY: 5,
        style: {
            fontFamily,
            fontSize: '18px',
        },
    },
    xaxis: {
        labels: {
            style: {
                fontFamily,
                fontSize: '14px',
            },
        },
        title: {
            style: {
                fontFamily,
                fontSize: '16px',
            },
        },
    },
    yaxis: {
        title: {
            style: {
                fontFamily,
                fontSize: '16px',
            },
        },
    },
};

export interface Serie {
    x: (string | number)[];
    y: (string | number)[];
}

export interface Series {
    [key: string]: Serie;
}

export interface ChartComponentInterface {
    computedStatistic: InputSignal<ComputedStatistic>;
    isPrintScreen: InputSignal<boolean>;
    options: Signal<BaseChartOptions>;
}

export interface SeriesFormated {
    data: (null | number | string)[];
    name: string;
}

export enum ChartTypeEnum {
    BAR = 'bar',
    LINE = 'line',
    RADIAL_BAR = 'radialBar',
    PIE = 'pie',
}

export type BaseChartOptions = {
    chart: ApexChart;
    colors?: string[];
    dataLabels?: ApexDataLabels;
    fill?: ApexFill;
    grid?: ApexGrid;
    labels?: string[];
    legend?: ApexLegend;
    noData?: ApexNoData;
    plotOptions?: ApexPlotOptions;
    series?: ApexAxisChartSeries | ApexNonAxisChartSeries;
    states?: ApexStates;
    stroke?: ApexStroke;
    subtitle?: ApexTitleSubtitle;
    title?: ApexTitleSubtitle;
    tooltip?: ApexTooltip;
    xaxis?: ApexXAxis;
    yaxis?: ApexYAxis;
};

@Injectable({
    providedIn: 'root',
})
export class ChartService {
    public alignMultiplesSeries(series: Series, categories: FeatureId[]): SeriesFormated[] {
        const alignedSeries: SeriesFormated[] = [];
        Object.keys(series || {}).map((key) => {
            const data = categories.map((category: FeatureId) => {
                const indexCategory = series?.[key].x.findIndex((x) => x === category);

                return indexCategory !== -1 ? series?.[key].y[indexCategory] : null;
            });

            alignedSeries.push({
                data,
                name: key,
            });
        });

        return alignedSeries;
    }

    public formatAxisText(title: string | undefined, unit: string | undefined | null): string {
        return [title, unit].filter(Boolean).join(' - ');
    }

    public getCategories(results: LineColumnComputedStatisticResult[]): FeatureId[] {
        const xCategories: FeatureId[] = [];
        results.forEach((result: LineColumnComputedStatisticResult) => {
            if (!xCategories.includes(result.x)) {
                xCategories.push(result.x);
            }
        });

        return xCategories;
    }

    public setSeries(computedStatistic: ComputedStatistic): Series | null {
        if (!computedStatistic.result) {
            return null;
        }
        const result: LineColumnComputedStatisticResults =
            computedStatistic.result as LineColumnComputedStatisticResults;

        return result.data.reduce((acc: Series, value: LineColumnComputedStatisticResult) => {
            const label = value.label ?? NO_LABEL;
            if (!acc[label]) {
                acc[label] = { x: [], y: [] };
            }

            acc[label].x.push(value.x);
            acc[label].y.push(Number(value.y));

            return acc;
        }, {});
    }
}
